import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { requerimientoService, ubicacionService, proveedorService } from '../services';
import { useAuth } from '../context/AuthContext';
import LineaTiempo from '../components/common/LineaTiempo';
import Modal from '../components/common/Modal';
import SearchSelect from '../components/common/SearchSelect';
import './DetalleRequerimiento.css';
import './NuevoRequerimiento.css';

const parseCedula = (cedulaString) => {
  if (!cedulaString) return { nac: 'V', num: '' };
  const parts = cedulaString.split('-');
  if (parts.length === 2) {
    const nac = parts[0].toUpperCase();
    if (nac === 'V' || nac === 'E') {
      return { nac, num: parts[1].replace(/\D/g, '') };
    }
  }
  const cleanNum = cedulaString.replace(/\D/g, '');
  const firstChar = cedulaString.charAt(0).toUpperCase();
  if (firstChar === 'V' || firstChar === 'E') {
    return { nac: firstChar, num: cedulaString.substring(1).replace(/\D/g, '') };
  }
  return { nac: 'V', num: cleanNum };
};

const DetalleRequerimiento = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    usuario,
    esPresidencia, esPresidente,
    esAdministracion, esAnalistaAdministracion,
    esVp, esAsistenteVp,
    transicionesDisponibles,
  } = useAuth();

  const isAdministracion = esAdministracion ? esAdministracion() : false;
  const isAnalistaAdministracion = esAnalistaAdministracion ? esAnalistaAdministracion() : false;
  const isAdministracionOAnalista = isAdministracion || isAnalistaAdministracion;
  const isVP = esVp ? esVp() : false;

  const [requerimiento, setRequerimiento] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [editando, setEditando] = useState(false);

  // Estados para edición
  const [estados, setEstados] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [parroquias, setParroquias] = useState([]);
  const [formData, setFormData] = useState({});
  const [cotizacionArchivo, setCotizacionArchivo] = useState(null);
  const [imagenProducto, setImagenProducto] = useState(null);
  const [tipoPago, setTipoPago] = useState('completo');
  const [montoPagoParcial, setMontoPagoParcial] = useState('');

  // Estados para panel de acciones
  const [proveedorForm, setProveedorForm] = useState({
    tipo_proveedor: '',
    proveedor: '',
    proveedor_nombre: '',
    proveedor_rif: '',
    costo_presupuestado: '',
    pago_movil_banco: '',
    pago_movil_telefono: '',
    pago_movil_cedula_nac: 'V',
    pago_movil_cedula_num: '',
  });

  // Estados de proveedores
  const [tiposProveedores, setTiposProveedores] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [tiposDocumentos, setTiposDocumentos] = useState([]);
  const [cargandoProveedores, setCargandoProveedores] = useState(false);

  // Modales de creación en caliente
  const [showNuevoProveedorModal, setShowNuevoProveedorModal] = useState(false);
  const [nuevoProveedorData, setNuevoProveedorData] = useState({
    tipo: '',
    nombre: '',
    tipo_documento: '',
    rif_numero: '',
  });
  const [errorProveedorModal, setErrorProveedorModal] = useState('');
  const [cargandoNuevoProveedor, setCargandoNuevoProveedor] = useState(false);

  const [showNuevaCategoriaModal, setShowNuevaCategoriaModal] = useState(false);
  const [nuevaCategoriaNombre, setNuevaCategoriaNombre] = useState('');
  const [cargandoNuevaCategoria, setCargandoNuevaCategoria] = useState(false);
  const [errorCategoriaModal, setErrorCategoriaModal] = useState('');

  const [procesandoAccion, setProcesandoAccion] = useState(false);
  const [criticidadSeleccionada, setCriticidadSeleccionada] = useState('');
  const [comentarioAccion, setComentarioAccion] = useState('');

  // Modal State
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'alert',
  });

  const showAlert = (message, type = 'alert', title = '') => {
    setModalConfig({
      isOpen: true,
      message,
      type,
      title: title || (type === 'error' ? 'Error' : 'Aviso'),
    });
  };

  useEffect(() => {
    cargarRequerimiento();
    cargarTiposProveedores();
    cargarTiposDocumentos();
  }, [id]);

  useEffect(() => {
    if (modoEdicion) {
      cargarEstados();
    }
  }, [modoEdicion]);

  const cargarRequerimiento = async () => {
    try {
      const data = await requerimientoService.getById(id);
      const solCed = parseCedula(data.solicitante_cedula);
      const pmCed = parseCedula(data.pago_movil_cedula);
      setRequerimiento(data);
      setFormData({
        solicitante_nombre: data.solicitante_nombre || '',
        solicitante_apellido: data.solicitante_apellido || '',
        solicitante_cedula_nac: solCed.nac,
        solicitante_cedula_num: solCed.num,
        solicitante_telefono: data.solicitante_telefono || '',
        ubicacion_estado: data.ubicacion_estado || '',
        ubicacion_municipio: data.ubicacion_municipio || '',
        ubicacion_parroquia: data.ubicacion_parroquia || '',
        descripcion: data.descripcion || '',
        monto_bs: data.monto_bs || '',
        costo_presupuestado: data.costo_presupuestado || '',
      });
      setProveedorForm({
        tipo_proveedor: data.tipo_proveedor || '',
        proveedor: data.proveedor || '',
        proveedor_nombre: data.proveedor_nombre || '',
        proveedor_rif: data.proveedor_rif || '',
        pago_movil_banco: data.pago_movil_banco || '',
        pago_movil_telefono: data.pago_movil_telefono || '',
        pago_movil_cedula_nac: pmCed.nac,
        pago_movil_cedula_num: pmCed.num,
      });
      if (data.tipo_proveedor) {
        cargarProveedoresPorTipo(data.tipo_proveedor);
      }
      setCriticidadSeleccionada(data.criticidad || '');
    } catch (error) {
      console.error('Error al cargar requerimiento:', error);
    } finally {
      setCargando(false);
    }
  };

  const cargarTiposProveedores = async () => {
    try {
      const data = await proveedorService.getTiposProveedores();
      const list = Array.isArray(data) ? data : (data?.results || []);
      setTiposProveedores(list);
    } catch (err) {
      console.error('Error al cargar tipos de proveedores:', err);
    }
  };

  const cargarTiposDocumentos = async () => {
    try {
      const data = await proveedorService.getTiposDocumentos();
      const list = Array.isArray(data) ? data : (data?.results || []);
      setTiposDocumentos(list);
    } catch (err) {
      console.error('Error al cargar tipos de documentos:', err);
    }
  };

  const cargarProveedoresPorTipo = async (tipoId) => {
    try {
      setCargandoProveedores(true);
      const data = await proveedorService.getProveedores({ tipo: tipoId });
      const list = Array.isArray(data) ? data : (data?.results || []);
      setProveedores(list);
    } catch (err) {
      console.error('Error al cargar proveedores:', err);
    } finally {
      setCargandoProveedores(false);
    }
  };

  const handleTipoProveedorChange = (tipoProv) => {
    const idStr = tipoProv ? tipoProv.id.toString() : '';
    setProveedorForm(prev => ({
      ...prev,
      tipo_proveedor: idStr,
      proveedor: '',
      proveedor_nombre: '',
      proveedor_rif: '',
    }));
    setProveedores([]);
    if (idStr) {
      cargarProveedoresPorTipo(tipoProv.id);
    }
  };

  const handleProveedorChange = (prov) => {
    const idStr = prov ? prov.id.toString() : '';
    setProveedorForm(prev => ({
      ...prev,
      proveedor: idStr,
      proveedor_nombre: prov ? prov.nombre : '',
      proveedor_rif: prov ? prov.rif : '',
    }));
  };

  const abrirModalNuevoProveedor = (sTerm = '') => {
    setNuevoProveedorData({
      tipo: proveedorForm.tipo_proveedor || '',
      nombre: sTerm,
      tipo_documento: tiposDocumentos[0]?.id || '',
      rif_numero: '',
    });
    setErrorProveedorModal('');
    setShowNuevoProveedorModal(true);
  };

  const abrirModalNuevaCategoria = (sTerm = '') => {
    setNuevaCategoriaNombre(sTerm);
    setErrorCategoriaModal('');
    setShowNuevaCategoriaModal(true);
  };

  const handleCrearCategoria = async (e) => {
    e.preventDefault();
    if (!nuevaCategoriaNombre.trim()) return;
    setErrorCategoriaModal('');
    setCargandoNuevaCategoria(true);
    try {
      const nuevaCat = await proveedorService.crearTipoProveedor({ nombre: nuevaCategoriaNombre });
      setTiposProveedores(prev => [...prev, nuevaCat].sort((a, b) => a.nombre.localeCompare(b.nombre)));

      setProveedorForm(prev => ({
        ...prev,
        tipo_proveedor: nuevaCat.id.toString(),
        proveedor: '',
        proveedor_nombre: '',
        proveedor_rif: '',
      }));
      setProveedores([]);
      setNuevaCategoriaNombre('');
      setShowNuevaCategoriaModal(false);
    } catch (err) {
      setErrorCategoriaModal(err.response?.data?.nombre || 'Error al crear la categoría.');
    } finally {
      setCargandoNuevaCategoria(false);
    }
  };

  const handleCrearProveedor = async (e) => {
    e.preventDefault();
    if (!nuevoProveedorData.tipo || !nuevoProveedorData.nombre || !nuevoProveedorData.tipo_documento || !nuevoProveedorData.rif_numero) {
      setErrorProveedorModal('Complete todos los campos obligatorios.');
      return;
    }
    setErrorProveedorModal('');
    setCargandoNuevoProveedor(true);
    try {
      const nuevoProv = await proveedorService.crearProveedor(nuevoProveedorData);
      setProveedores(prev => [...prev, nuevoProv].sort((a, b) => a.nombre.localeCompare(b.nombre)));
      setProveedorForm(prev => ({
        ...prev,
        proveedor: nuevoProv.id.toString(),
        proveedor_nombre: nuevoProv.nombre,
        proveedor_rif: nuevoProv.rif,
      }));
      setShowNuevoProveedorModal(false);
    } catch (err) {
      const apiError = err.response?.data;
      if (apiError && typeof apiError === 'object') {
        if (apiError.rif_numero) {
          setErrorProveedorModal(`RIF: ${apiError.rif_numero}`);
        } else if (apiError.rif) {
          setErrorProveedorModal(`RIF: ${apiError.rif}`);
        } else {
          const firstKey = Object.keys(apiError)[0];
          setErrorProveedorModal(`${firstKey}: ${apiError[firstKey]}`);
        }
      } else {
        setErrorProveedorModal(err.response?.data?.detail || 'Error al crear el proveedor.');
      }
    } finally {
      setCargandoNuevoProveedor(false);
    }
  };

  const cargarEstados = async () => {
    try {
      const data = await ubicacionService.getEstados();
      setEstados(data);
    } catch (error) {
      console.error('Error al cargar estados:', error);
    }
  };

  const cargarMunicipios = async (estadoId) => {
    try {
      const data = await ubicacionService.getMunicipios(estadoId);
      setMunicipios(data);
      setParroquias([]);
    } catch (error) {
      console.error('Error al cargar municipios:', error);
    }
  };

  const cargarParroquias = async (municipioId) => {
    try {
      const data = await ubicacionService.getParroquias(municipioId);
      setParroquias(data);
    } catch (error) {
      console.error('Error al cargar parroquias:', error);
    }
  };

  const handleEditar = () => {
    setModoEdicion(true);
    if (formData.ubicacion_estado) {
      cargarMunicipios(formData.ubicacion_estado);
    }
    if (formData.ubicacion_municipio) {
      cargarParroquias(formData.ubicacion_municipio);
    }
  };

  const handleCancelarEdicion = () => {
    setModoEdicion(false);
    setEditando(false);
  };

  const handleGuardarEdicion = async () => {
    try {
      setEditando(true);
      const data = new FormData();

      Object.keys(formData).forEach(key => {
        if (key !== 'solicitante_cedula_nac' && key !== 'solicitante_cedula_num') {
          if (formData[key] !== null && formData[key] !== undefined) {
            data.append(key, formData[key]);
          }
        }
      });

      // Combinar cédula del solicitante para enviar al backend
      const solicitante_cedula = `${formData.solicitante_cedula_nac}-${formData.solicitante_cedula_num}`;
      data.append('solicitante_cedula', solicitante_cedula);

      if (cotizacionArchivo) {
        data.append('cotizacion_archivo', cotizacionArchivo);
      }
      if (imagenProducto) {
        data.append('imagen_producto', imagenProducto);
      }

      const response = await requerimientoService.update(id, data);
      setRequerimiento(response);
      setModoEdicion(false);
      setEditando(false);
      await cargarRequerimiento();
    } catch (error) {
      console.error('Error al actualizar:', error);
      showAlert('Error al actualizar el requerimiento', 'error');
    } finally {
      setEditando(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;

    // Forzar solo números para teléfonos y cédulas
    if (name === 'solicitante_telefono' || name === 'solicitante_cedula_num') {
      finalValue = value.replace(/\D/g, '');
    }
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const handleEstadoChange = (e) => {
    const estadoId = e.target.value;
    setFormData(prev => ({
      ...prev,
      ubicacion_estado: estadoId,
      ubicacion_municipio: '',
      ubicacion_parroquia: '',
    }));
    setMunicipios([]);
    setParroquias([]);
    if (estadoId) cargarMunicipios(estadoId);
  };

  const handleMunicipioChange = (e) => {
    const municipioId = e.target.value;
    setFormData(prev => ({
      ...prev,
      ubicacion_municipio: municipioId,
      ubicacion_parroquia: '',
    }));
    setParroquias([]);
    if (municipioId) cargarParroquias(municipioId);
  };

  // Acciones de ciclo de vida
  const handlePonerRevision = async () => {
    try {
      setProcesandoAccion(true);
      await requerimientoService.cambiarEstado(id, 'por_revision', 'Enviado a revisión.');
      await cargarRequerimiento();
    } catch (error) {
      console.error(error);
      showAlert('Error al poner en revisión', 'error');
    } finally {
      setProcesandoAccion(false);
    }
  };

  const handleAprobarVPComercialOperativa = async () => {
    if (!criticidadSeleccionada) {
      showAlert('Debe asignar una criticidad (Alta, Media o Baja) para aprobar.', 'warning');
      return;
    }
    try {
      setProcesandoAccion(true);
      // 1. Asignar criticidad
      await requerimientoService.aplicarCriticidad(id, criticidadSeleccionada);
      // 2. Aprobar — el estado es genérico para cualquier VP
      let nuevoEstado = 'aprobado_vicepresidencia';
      await requerimientoService.cambiarEstado(id, nuevoEstado, comentarioAccion || 'Aprobado por Vicepresidencia.');
      await cargarRequerimiento();
      setComentarioAccion('');
    } catch (error) {
      console.error(error);
      showAlert('Error al aprobar el requerimiento', 'error');
    } finally {
      setProcesandoAccion(false);
    }
  };

  const handleRechazarVPComercialOperativa = async () => {
    if (!comentarioAccion.trim()) {
      showAlert('La observación de rechazo es obligatoria para rechazar el requerimiento.', 'warning');
      return;
    }
    try {
      setProcesandoAccion(true);
      let nuevoEstado = 'rechazado_vicepresidencia';
      await requerimientoService.cambiarEstado(id, nuevoEstado, comentarioAccion);
      await cargarRequerimiento();
      setComentarioAccion('');
    } catch (error) {
      console.error(error);
      showAlert('Error al rechazar el requerimiento', 'error');
    } finally {
      setProcesandoAccion(false);
    }
  };

  const handleRecibirVPAdministrativa = async () => {
    try {
      setProcesandoAccion(true);
      // Nuevo estado de recepción por Administración
      await requerimientoService.cambiarEstado(id, 'recibido_administracion', 'Requerimiento recibido por Administración.');
      await cargarRequerimiento();
    } catch (error) {
      console.error(error);
      showAlert('Error al recibir el requerimiento', 'error');
    } finally {
      setProcesandoAccion(false);
    }
  };

  const handleAprobarVPAdministrativa = async () => {
    const yaHayProveedor = requerimiento.tiene_presupuesto && (requerimiento.proveedor_nombre || requerimiento.proveedor);

    if (!yaHayProveedor) {
      if (!proveedorForm.tipo_proveedor || !proveedorForm.proveedor || !proveedorForm.costo_presupuestado) {
        showAlert('Debe seleccionar la categoría, el proveedor y el costo presupuestado para poder aprobar.', 'warning');
        return;
      }
      if (!proveedorForm.pago_movil_banco.trim() || !proveedorForm.pago_movil_telefono.trim() || !proveedorForm.pago_movil_cedula_num.trim()) {
        showAlert('Debe registrar todos los datos de Pago Móvil (Banco, Teléfono y Cédula).', 'warning');
        return;
      }
      if (!cotizacionArchivo && !imagenProducto && !requerimiento.cotizacion_archivo && !requerimiento.imagen_producto) {
        showAlert('Es obligatorio adjuntar al menos la Cotización o una Imagen del producto al asignar el proveedor.', 'warning');
        return;
      }
    }
    try {
      setProcesandoAccion(true);
      if (!yaHayProveedor) {
        const formData = new FormData();
        formData.append('tipo_proveedor', proveedorForm.tipo_proveedor);
        formData.append('proveedor', proveedorForm.proveedor);
        formData.append('proveedor_nombre', proveedorForm.proveedor_nombre);
        formData.append('proveedor_rif', proveedorForm.proveedor_rif);
        formData.append('costo_presupuestado', proveedorForm.costo_presupuestado);
        if (proveedorForm.pago_movil_banco) formData.append('pago_movil_banco', proveedorForm.pago_movil_banco);
        if (proveedorForm.pago_movil_telefono) formData.append('pago_movil_telefono', proveedorForm.pago_movil_telefono);
        const cedula = proveedorForm.pago_movil_cedula_num ? `${proveedorForm.pago_movil_cedula_nac || 'V'}-${proveedorForm.pago_movil_cedula_num}` : '';
        if (cedula) formData.append('pago_movil_cedula', cedula);

        if (cotizacionArchivo) formData.append('cotizacion_archivo', cotizacionArchivo);
        if (imagenProducto) formData.append('imagen_producto', imagenProducto);

        await requerimientoService.asignarProveedor(id, formData);
      }
      // Determinar el estado de aprobación según qué rol está actuando
      let nuevoEstado;
      if (isAdministracionOAnalista) {
        nuevoEstado = 'aprobado_administracion';
      } else {
        // VP Administrativa pura o asistente
        nuevoEstado = 'aprobado_vicepresidencia';
      }
      await requerimientoService.cambiarEstado(id, nuevoEstado, comentarioAccion || 'Aprobado.');
      await cargarRequerimiento();
      setComentarioAccion('');
    } catch (error) {
      console.error(error);
      showAlert('Error al aprobar el requerimiento', 'error');
    } finally {
      setProcesandoAccion(false);
    }
  };


  const handleRechazarVPAdministrativa = async () => {
    if (!comentarioAccion.trim()) {
      showAlert('La observación de rechazo es obligatoria para rechazar el requerimiento.', 'warning');
      return;
    }
    try {
      setProcesandoAccion(true);
      let nuevoEstado;
      if (isAdministracionOAnalista) {
        nuevoEstado = 'rechazado_administracion';
      } else {
        nuevoEstado = 'rechazado_vicepresidencia';
      }
      await requerimientoService.cambiarEstado(id, nuevoEstado, comentarioAccion);
      await cargarRequerimiento();
      setComentarioAccion('');
    } catch (error) {
      console.error(error);
      showAlert('Error al rechazar el requerimiento', 'error');
    } finally {
      setProcesandoAccion(false);
    }
  };

  const handlePagarRequerimiento = async () => {
    let montoApagar = 0;
    const costoTotal = parseFloat(requerimiento.costo_presupuestado || requerimiento.monto_bs || 0);
    const montoPagado = parseFloat(requerimiento.monto_pagado_bs || 0);
    const montoRestante = costoTotal - montoPagado;

    if (tipoPago === 'completo') {
      montoApagar = montoRestante;
    } else {
      montoApagar = parseFloat(montoPagoParcial);
      if (isNaN(montoApagar) || montoApagar <= 0) {
        showAlert('Ingrese un monto válido mayor a cero.', 'warning');
        return;
      }
      if (montoApagar > montoRestante) {
        showAlert(`El monto no puede superar el restante (${montoRestante.toFixed(2)} Bs).`, 'warning');
        return;
      }
    }

    try {
      setProcesandoAccion(true);
      await requerimientoService.registrarPago(id, montoApagar);
      await cargarRequerimiento();
      setMontoPagoParcial('');
      setTipoPago('completo');
      showAlert('Pago registrado exitosamente', 'success');
    } catch (error) {
      console.error(error);
      showAlert(error.response?.data?.detail || 'Error al registrar el pago', 'error');
    } finally {
      setProcesandoAccion(false);
    }
  };

  const handleDownload = async (url, type) => {
    try {
      setProcesandoAccion(true);
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network error');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      let filename = 'descarga';
      try {
        const urlParts = new URL(url, window.location.origin).pathname.split('/');
        filename = urlParts[urlParts.length - 1] || `${type}_${id}`;
      } catch (e) {
        filename = `${type}_${id}`;
      }
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading:', error);
      window.open(url, '_blank'); // fallback
    } finally {
      setProcesandoAccion(false);
    }
  };

  const getEstadoColor = (estado) => {
    const colores = {
      elaborado: '#2196f3',
      por_revision: '#ff9800',
      aprobado_vicepresidencia: '#4caf50',
      rechazado_vicepresidencia: '#f44336',
      aprobado_vp_comercial: '#4caf50',
      rechazado_vp_comercial: '#f44336',
      aprobado_vp_operativa: '#4caf50',
      rechazado_vp_operativa: '#f44336',
      aprobado_vp_administrativa: '#03a9f4',
      rechazado_vp_administrativa: '#f44336',
      recibido_administracion: '#00bcd4',
      aprobado_administracion: '#8bc34a',
      rechazado_administracion: '#f44336',
      recibido_vp_administrativa: '#00bcd4',   // legacy
      pagado: '#9c27b0',
    };
    return colores[estado] || '#9e9e9e';
  };

  const getCriticidadLabel = (crit) => {
    const labels = {
      alta: 'Alta 🔴',
      media: 'Media 🟡',
      baja: 'Baja 🟢',
    };
    return labels[crit] || 'Sin asignar';
  };

  const renderActionPanel = () => {
    const estado = requerimiento.estado_actual;

    // ─── Vicepresidencia (Cualquiera) ──────────────────────────────────────────
    if (isVP) {
      const vpUsuarioId = usuario?.vicepresidencia?.id || usuario?.vicepresidencia;
      const vpReqId = requerimiento.vicepresidencia?.id || requerimiento.vicepresidencia;
      const matchesVp = (vpUsuarioId === vpReqId);

      if (matchesVp) {
        if (estado === 'elaborado') {
          return (
            <div className="panel-acciones">
              <h3>Panel de Acciones (Vicepresidencia)</h3>
              <p>Este requerimiento está en estado <strong>Elaborado</strong> y debe ponerse en revisión.</p>
              <button className="btn-primary" onClick={handlePonerRevision} disabled={procesandoAccion}>
                {procesandoAccion ? 'Enviando...' : 'Poner en Revisión'}
              </button>
            </div>
          );
        }

        if (estado === 'por_revision') {
          return (
            <div className="panel-acciones">
              <h3>Panel de Acciones (Vicepresidencia)</h3>
              <div className="criticidad-selection">
                <label className="section-label">Asignar Criticidad:</label>
                <div className="criticidad-buttons">
                  {['alta', 'media', 'baja'].map(c => (
                    <button key={c} type="button"
                      className={`btn-crit ${c} ${criticidadSeleccionada === c ? 'selected' : ''}`}
                      onClick={() => setCriticidadSeleccionada(c)}
                    >
                      {c === 'alta' ? '🔴' : c === 'media' ? '🟡' : '🟢'} {c.charAt(0).toUpperCase() + c.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="comentario-group">
                <label className="section-label">Observaciones / Comentario:</label>
                <textarea className="input-comentario" placeholder="Comentario (requerido para rechazo)"
                  value={comentarioAccion} onChange={e => setComentarioAccion(e.target.value)} rows="3" />
              </div>
              <div className="acciones-buttons">
                <button className="btn-success" onClick={handleAprobarVPComercialOperativa} disabled={procesandoAccion}>
                  {procesandoAccion ? 'Procesando...' : 'Aprobar y Enviar a Administración'}
                </button>
                <button className="btn-danger" onClick={handleRechazarVPComercialOperativa} disabled={procesandoAccion}>
                  {procesandoAccion ? 'Procesando...' : 'Rechazar'}
                </button>
              </div>
            </div>
          );
        }
      }
    }
    // ─── Administración (o Presidencia) ──────────────────────────────────────
    if (isAdministracionOAnalista) {
      if (
        ['aprobado_vicepresidencia', 'aprobado_vp_comercial', 'aprobado_vp_operativa', 'aprobado_vp_administrativa',
          'recibido_vp_administrativa'].includes(estado)
      ) {
        return (
          <div className="panel-acciones">
            <h3>Panel de Acciones (Administración)</h3>
            <p>El requerimiento fue aprobado por su VP originaria y está disponible para ser recibido y procesado.</p>
            <button className="btn-primary" onClick={handleRecibirVPAdministrativa} disabled={procesandoAccion}>
              {procesandoAccion ? 'Procesando...' : 'Recibir Requerimiento'}
            </button>
          </div>
        );
      }

      if (estado === 'recibido_administracion' || estado === 'recibido_vp_administrativa') {
        const yaHayProveedor = requerimiento.tiene_presupuesto && (requerimiento.proveedor_nombre || requerimiento.proveedor);

        return (
          <div className="panel-acciones">
            <h3>Panel de Acciones (Administración)</h3>

            {!yaHayProveedor && requerimiento.observacion_proveedor && (
              <div className="observacion-proveedor-alert">
                <h4>
                  <span>ℹ️</span> Observación / Detalles del Proveedor (Pendiente)
                </h4>
                <p>
                  {requerimiento.observacion_proveedor}
                </p>
                <p className="hint-text">
                  * Utilice esta información y el archivo de cotización adjunto (si aplica) para registrar formalmente al proveedor usando los campos a continuación.
                </p>
              </div>
            )}

            {yaHayProveedor ? (
              <div className="proveedor-ya-registrado">
                <div className="proveedor-badge">
                  <span className="badge-icon">✅</span>
                  <div>
                    <strong>Proveedor registrado por el analista</strong>
                    <p>Este requerimiento ya cuenta con datos de proveedor. Revíselos antes de aprobar.</p>
                  </div>
                </div>
                <div className="form-row" style={{ marginTop: '12px' }}>
                  <div className="form-group">
                    <label>Nombre del Proveedor</label>
                    <input type="text" value={requerimiento.proveedor_nombre} readOnly style={{ background: 'var(--bg-secondary, #f9fafb)', cursor: 'not-allowed' }} />
                  </div>
                  <div className="form-group">
                    <label>RIF del Proveedor</label>
                    <input type="text" value={requerimiento.proveedor_rif} readOnly style={{ background: 'var(--bg-secondary, #f9fafb)', cursor: 'not-allowed' }} />
                  </div>
                </div>
                {requerimiento.pago_movil_banco && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Banco</label>
                      <input type="text" value={requerimiento.pago_movil_banco} readOnly style={{ background: 'var(--bg-secondary, #f9fafb)', cursor: 'not-allowed' }} />
                    </div>
                    <div className="form-group">
                      <label>Teléfono</label>
                      <input type="text" value={requerimiento.pago_movil_telefono || '—'} readOnly style={{ background: 'var(--bg-secondary, #f9fafb)', cursor: 'not-allowed' }} />
                    </div>
                    <div className="form-group">
                      <label>Cédula Titular</label>
                      <input type="text" value={requerimiento.pago_movil_cedula || '—'} readOnly style={{ background: 'var(--bg-secondary, #f9fafb)', cursor: 'not-allowed' }} />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="proveedor-fields">
                <label className="section-label">Datos del Proveedor (Requeridos para Aprobar):</label>
                <div className="form-row" style={{ marginBottom: '12px' }}>
                  <div className="form-group">
                    <label>Categoría de Proveedor *</label>
                    <SearchSelect
                      options={tiposProveedores}
                      value={proveedorForm.tipo_proveedor}
                      onChange={handleTipoProveedorChange}
                      onCreateNew={abrirModalNuevaCategoria}
                      createNewText="+ Crear nueva categoría"
                      placeholder="Busque o seleccione categoría..."
                      labelKey="nombre"
                      valueKey="id"
                    />
                  </div>
                  <div className="form-group">
                    <label>Proveedor *</label>
                    <SearchSelect
                      options={proveedores}
                      value={proveedorForm.proveedor}
                      onChange={handleProveedorChange}
                      onCreateNew={abrirModalNuevoProveedor}
                      createNewText="+ Crear nuevo proveedor"
                      placeholder="Busque o seleccione proveedor..."
                      labelKey="nombre"
                      valueKey="id"
                      disabled={!proveedorForm.tipo_proveedor}
                      loading={cargandoProveedores}
                      emptyMessage={proveedorForm.tipo_proveedor ? "No se encontraron proveedores" : "Seleccione una categoría primero"}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nombre del Proveedor (autocompletado)</label>
                    <input
                      type="text"
                      value={proveedorForm.proveedor_nombre}
                      placeholder="Seleccione un proveedor..."
                      disabled
                    />
                  </div>
                  <div className="form-group">
                    <label>RIF del Proveedor (autocompletado)</label>
                    <input
                      type="text"
                      value={proveedorForm.proveedor_rif}
                      placeholder="Seleccione un proveedor..."
                      disabled
                    />
                  </div>
                  <div className="form-group">
                    <label>Costo Presupuestado (Bs.) *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={proveedorForm.costo_presupuestado}
                      onChange={(e) => setProveedorForm({ ...proveedorForm, costo_presupuestado: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <label className="section-label">Archivos (Obligatorio al menos uno):</label>
                <div className="file-upload-grid" style={{ marginBottom: '24px' }}>
                  <label className={`file-upload-card ${cotizacionArchivo ? 'has-file' : ''}`}>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.gif"
                      onChange={(e) => setCotizacionArchivo(e.target.files[0])}
                      hidden
                    />
                    <div className="file-upload-icon">📄</div>
                    <div className="file-upload-title">Cotización (PDF/Imagen)</div>
                    <div className="file-upload-desc">Obligatorio si no hay foto</div>
                    {cotizacionArchivo && (
                      <div className="file-upload-name">{cotizacionArchivo.name}</div>
                    )}
                  </label>

                  <label className={`file-upload-card ${imagenProducto ? 'has-file' : ''}`}>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.gif"
                      onChange={(e) => setImagenProducto(e.target.files[0])}
                      hidden
                    />
                    <div className="file-upload-icon">🖼️</div>
                    <div className="file-upload-title">Imagen del Producto</div>
                    <div className="file-upload-desc">Obligatorio si no hay cotización</div>
                    {imagenProducto && (
                      <div className="file-upload-name">{imagenProducto.name}</div>
                    )}
                  </label>
                </div>
                <label className="section-label">Datos de Pago Móvil (Obligatorios):</label>
                <div className="form-row">
                  <div className="form-group">
                    <label>Banco *</label>
                    <input
                      type="text"
                      value={proveedorForm.pago_movil_banco}
                      onChange={(e) => setProveedorForm({ ...proveedorForm, pago_movil_banco: e.target.value })}
                      placeholder="Ej. Banesco"
                    />
                  </div>
                  <div className="form-group">
                    <label>Teléfono Celular *</label>
                    <input
                      type="text"
                      value={proveedorForm.pago_movil_telefono}
                      onChange={(e) => setProveedorForm({ ...proveedorForm, pago_movil_telefono: e.target.value.replace(/\D/g, '') })}
                      placeholder="Ej. 04141234567"
                    />
                  </div>
                  <div className="form-group">
                    <label>Cédula del Titular *</label>
                    <div className="cedula-input-group">
                      <select
                        value={proveedorForm.pago_movil_cedula_nac}
                        onChange={(e) => setProveedorForm({ ...proveedorForm, pago_movil_cedula_nac: e.target.value })}
                        className="select-nacionalidad"
                      >
                        <option value="V">V</option>
                        <option value="E">E</option>
                      </select>
                      <input
                        type="text"
                        value={proveedorForm.pago_movil_cedula_num}
                        onChange={(e) => setProveedorForm({ ...proveedorForm, pago_movil_cedula_num: e.target.value.replace(/\D/g, '') })}
                        placeholder="Ej. 20123456"
                        className="input-cedula-num"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="comentario-group">
              <label className="section-label">Observaciones / Comentario:</label>
              <textarea
                className="input-comentario"
                placeholder="Escriba un comentario (requerido para rechazo)"
                value={comentarioAccion}
                onChange={(e) => setComentarioAccion(e.target.value)}
                rows="3"
              />
            </div>

            <div className="acciones-buttons">
              <button className="btn-success" onClick={handleAprobarVPAdministrativa} disabled={procesandoAccion}>
                {procesandoAccion ? 'Procesando...' : 'Aprobar Requerimiento'}
              </button>
              <button className="btn-danger" onClick={handleRechazarVPAdministrativa} disabled={procesandoAccion}>
                {procesandoAccion ? 'Procesando...' : 'Rechazar'}
              </button>
            </div>
          </div>
        );
      }

      if (['aprobado_administracion', 'aprobado_vp_administrativa', 'pagado_parcial'].includes(estado)) {
        const costoTotal = parseFloat(requerimiento.costo_presupuestado || requerimiento.monto_bs || 0);
        const montoPagado = parseFloat(requerimiento.monto_pagado_bs || 0);
        const montoRestante = costoTotal - montoPagado;

        return (
          <div className="panel-acciones">
            <h3>Panel de Acciones (Registro de Pago)</h3>

            <div className="resumen-pago" style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }}>
              <div><strong>Costo Total:</strong> {costoTotal.toLocaleString('es', { minimumFractionDigits: 2 })} Bs</div>
              <div style={{ color: '#059669' }}><strong>Abonado:</strong> {montoPagado.toLocaleString('es', { minimumFractionDigits: 2 })} Bs</div>
              <div style={{ color: '#dc2626' }}><strong>Restante:</strong> {montoRestante.toLocaleString('es', { minimumFractionDigits: 2 })} Bs</div>
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label><strong>Tipo de Pago:</strong></label>
              <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="radio" name="tipoPago" value="completo" checked={tipoPago === 'completo'} onChange={() => setTipoPago('completo')} />
                  Pago Completo ({montoRestante.toLocaleString('es', { minimumFractionDigits: 2 })} Bs)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="radio" name="tipoPago" value="parcial" checked={tipoPago === 'parcial'} onChange={() => setTipoPago('parcial')} />
                  Pago Parcial
                </label>
              </div>
            </div>

            {tipoPago === 'parcial' && (
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label>Monto a Abonar (Bs):</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={montoRestante}
                  value={montoPagoParcial}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (val && parseFloat(val) > montoRestante) {
                      val = montoRestante.toString();
                    }
                    setMontoPagoParcial(val);
                  }}
                  placeholder="Ej. 15000.00"
                  style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>
            )}

            <button className="btn-success" onClick={handlePagarRequerimiento} disabled={procesandoAccion}>
              {procesandoAccion ? 'Procesando...' : 'Registrar Pago'}
            </button>
          </div>
        );
      }
    }

    return null;
  };


  if (cargando) {
    return <div className="loading">Cargando...</div>;
  }

  if (!requerimiento) {
    return <div className="error">Requerimiento no encontrado</div>;
  }

  // Modo edición de analista (solo si está en borrador/elaborado y es su creador)
  if (modoEdicion) {
    return (
      <div className="detalle-requerimiento">
        <div className="detalle-header">
          <button className="btn-back" onClick={handleCancelarEdicion}>
            ← Cancelar
          </button>
          <h2>Editando: {requerimiento.correlativo}</h2>
        </div>

        <div className="form-requerimiento">
          <div className="form-section">
            <h3>Información del Solicitante</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Nombre</label>
                <input type="text" name="solicitante_nombre" value={formData.solicitante_nombre} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Apellido</label>
                <input type="text" name="solicitante_apellido" value={formData.solicitante_apellido} onChange={handleChange} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Cédula</label>
                <div className="cedula-input-group">
                  <select
                    name="solicitante_cedula_nac"
                    value={formData.solicitante_cedula_nac}
                    onChange={handleChange}
                    className="select-nacionalidad"
                  >
                    <option value="V">V</option>
                    <option value="E">E</option>
                  </select>
                  <input
                    type="text"
                    name="solicitante_cedula_num"
                    value={formData.solicitante_cedula_num}
                    onChange={handleChange}
                    placeholder="Ej. 12345678"
                    className="input-cedula-num"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Teléfono</label>
                <input
                  type="text"
                  name="solicitante_telefono"
                  value={formData.solicitante_telefono}
                  onChange={handleChange}
                  placeholder="Ej: 04121234567"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Ubicación Geográfica</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Estado</label>
                <select name="ubicacion_estado" value={formData.ubicacion_estado} onChange={handleEstadoChange}>
                  <option value="">Seleccione</option>
                  {estados.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Municipio</label>
                <select name="ubicacion_municipio" value={formData.ubicacion_municipio} onChange={handleMunicipioChange} disabled={!formData.ubicacion_estado}>
                  <option value="">Seleccione</option>
                  {municipios.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Parroquia</label>
                <select name="ubicacion_parroquia" value={formData.ubicacion_parroquia} onChange={handleChange} disabled={!formData.ubicacion_municipio}>
                  <option value="">Seleccione</option>
                  {parroquias.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Información del Requerimiento</h3>
            <div className="form-group">
              <label>Descripción</label>
              <textarea name="descripcion" value={formData.descripcion} onChange={handleChange} rows="4" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Monto Solicitado (Bs.)</label>
                <input type="number" name="monto_bs" value={formData.monto_bs} onChange={handleChange} step="0.01" />
              </div>
              <div className="form-group">
                <label>Costo Presupuestado (Bs. - Opcional)</label>
                <input type="number" name="costo_presupuestado" value={formData.costo_presupuestado || ''} onChange={handleChange} step="0.01" />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Archivos (opcional - solo si desea reemplazar)</h3>
            <div className="form-group">
              <label>Cotización</label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif" onChange={(e) => setCotizacionArchivo(e.target.files[0])} />
            </div>
            <div className="form-group">
              <label>Imagen del Producto</label>
              <input type="file" accept=".jpg,.jpeg,.png,.gif" onChange={(e) => setImagenProducto(e.target.files[0])} />
            </div>
          </div>

          <div className="form-actions">
            <button className="btn-secondary" onClick={handleCancelarEdicion}>Cancelar</button>
            <button className="btn-primary" onClick={handleGuardarEdicion} disabled={editando}>
              {editando ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Modo visualización
  return (
    <div className="detalle-requerimiento">
      <div className="detalle-header">
        <button className="btn-back" onClick={() => navigate(-1)}>
          ← Volver
        </button>
        <h2>Requerimiento: {requerimiento.correlativo}</h2>
        <span
          className="estado-badge"
          style={{ backgroundColor: getEstadoColor(requerimiento.estado_actual) }}
        >
          {requerimiento.estado_display}
        </span>
      </div>

      <div className="detalle-grid">
        <div className="detalle-seccion">
          <h3>👤 Información del Solicitante</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Nombre completo:</label>
              <span>{requerimiento.solicitante_nombre} {requerimiento.solicitante_apellido}</span>
            </div>
            <div className="info-item">
              <label>Cédula:</label>
              <span>{requerimiento.solicitante_cedula}</span>
            </div>
            <div className="info-item">
              <label>Teléfono:</label>
              <span>{requerimiento.solicitante_telefono}</span>
            </div>
          </div>
        </div>

        <div className="detalle-seccion">
          <h3>📍 Ubicación del Solicitante</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Estado:</label>
              <span>{requerimiento.ubicacion_estado_nombre}</span>
            </div>
            <div className="info-item">
              <label>Municipio:</label>
              <span>{requerimiento.ubicacion_municipio_nombre}</span>
            </div>
            <div className="info-item">
              <label>Parroquia:</label>
              <span>{requerimiento.ubicacion_parroquia_nombre}</span>
            </div>
          </div>
        </div>

        <div className="detalle-seccion">
          <h3>📋 Detalle del Requerimiento</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Vicepresidencia emisora:</label>
              <span>{requerimiento.vicepresidencia_nombre}</span>
            </div>
            <div className="info-item">
              <label>Gerencia asignada:</label>
              <span>{requerimiento.gerencia_nombre}</span>
            </div>
            <div className="info-item">
              <label>Monto Solicitado (Bs):</label>
              <span>{parseFloat(requerimiento.monto_bs).toLocaleString('es', { minimumFractionDigits: 2 })} Bs.</span>
            </div>
            {requerimiento.costo_presupuestado && (
              <div className="info-item">
                <label>Costo Presupuestado (Bs):</label>
                <span>{parseFloat(requerimiento.costo_presupuestado).toLocaleString('es', { minimumFractionDigits: 2 })} Bs.</span>
              </div>
            )}
            <div className="info-item">
              <label>Criticidad:</label>
              <span><strong>{getCriticidadLabel(requerimiento.criticidad)}</strong></span>
            </div>
          </div>
          <div className="info-item full-width" style={{ marginTop: '16px' }}>
            <label>Descripción detallada:</label>
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{requerimiento.descripcion}</p>
          </div>
        </div>

        {/* Sección de Datos Cambiarios y BCV */}
        <div className="detalle-seccion">
          <h3>💵 Datos Cambiarios (BCV)</h3>
          <div className="info-grid">
            {requerimiento.tasa_bcv_creacion ? (
              <>
                <div className="info-item">
                  <label>Tasa BCV Creación:</label>
                  <span>{parseFloat(requerimiento.tasa_bcv_creacion).toLocaleString('es', { minimumFractionDigits: 4 })} Bs./USD</span>
                </div>
                <div className="info-item">
                  <label>Monto USD (Creación):</label>
                  <span>${parseFloat(requerimiento.monto_usd_creacion).toLocaleString('es', { minimumFractionDigits: 2 })} USD</span>
                </div>
              </>
            ) : (
              <div className="no-rate-warning">
                ⚠️ Sin tasa cambiaria registrada a la creación (la tasa BCV no estaba disponible en el sistema).
              </div>
            )}

            {requerimiento.tasa_bcv_aprobacion && (
              <>
                <div className="info-item">
                  <label>Tasa BCV Aprobación (Histórico):</label>
                  <span>{parseFloat(requerimiento.tasa_bcv_aprobacion).toLocaleString('es', { minimumFractionDigits: 4 })} Bs./USD</span>
                </div>
                <div className="info-item">
                  <label>Monto USD (Aprobado):</label>
                  <span>${parseFloat(requerimiento.monto_usd_aprobacion).toLocaleString('es', { minimumFractionDigits: 2 })} USD</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Historial de Pagos Parciales (si existen) */}
        {requerimiento.pagos && requerimiento.pagos.length > 0 && (
          <div className="detalle-seccion">
            <h3>💰 Historial de Pagos</h3>
            <div className="table-responsive">
              <table className="tabla-pagos" style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--bg-subtle, #f1f5f9)', textAlign: 'left', color: 'var(--text-color, inherit)' }}>
                    <th style={{ padding: '10px', borderBottom: '1px solid var(--border-light, #e2e8f0)' }}>Fecha</th>
                    <th style={{ padding: '10px', borderBottom: '1px solid var(--border-light, #e2e8f0)' }}>Monto (Bs)</th>
                    <th style={{ padding: '10px', borderBottom: '1px solid var(--border-light, #e2e8f0)' }}>Tasa BCV</th>
                    <th style={{ padding: '10px', borderBottom: '1px solid var(--border-light, #e2e8f0)' }}>Equivalente (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {requerimiento.pagos.map((pago) => (
                    <tr key={pago.id}>
                      <td style={{ padding: '10px', borderBottom: '1px solid var(--border-light, #e2e8f0)' }}>
                        {new Date(pago.fecha).toLocaleString()}
                      </td>
                      <td style={{ padding: '10px', borderBottom: '1px solid var(--border-light, #e2e8f0)', fontWeight: 'bold' }}>
                        {parseFloat(pago.monto_bs).toLocaleString('es', { minimumFractionDigits: 2 })} Bs
                      </td>
                      <td style={{ padding: '10px', borderBottom: '1px solid var(--border-light, #e2e8f0)' }}>
                        {pago.tasa_bcv ? `${parseFloat(pago.tasa_bcv).toLocaleString('es', { minimumFractionDigits: 4 })} Bs./USD` : 'N/A'}
                      </td>
                      <td style={{ padding: '10px', borderBottom: '1px solid var(--border-light, #e2e8f0)' }}>
                        {pago.monto_usd ? `$${parseFloat(pago.monto_usd).toLocaleString('es', { minimumFractionDigits: 2 })} USD` : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Sección de Presupuesto y Proveedor */}
        <div className="detalle-seccion">
          <h3>🏢 Información de Proveedor y Financiamiento</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>¿Posee Proveedor?:</label>
              <span>{requerimiento.tiene_presupuesto ? 'Sí ✅' : 'No ❌'}</span>
            </div>
            {requerimiento.proveedor_nombre ? (
              <>
                <div className="info-item">
                  <label>Nombre del Proveedor:</label>
                  <span>{requerimiento.proveedor_nombre}</span>
                </div>
                <div className="info-item">
                  <label>RIF del Proveedor:</label>
                  <span>{requerimiento.proveedor_rif}</span>
                </div>
                {requerimiento.pago_movil_banco && (
                  <div className="info-item full-width">
                    <label>Datos de Pago Móvil:</label>
                    <span>
                      Banco: {requerimiento.pago_movil_banco} |
                      Teléfono: {requerimiento.pago_movil_telefono} |
                      Cédula: {requerimiento.pago_movil_cedula}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="info-item full-width" style={{ backgroundColor: '#fdf2f8' }}>
                <span style={{ color: '#db2777', fontSize: '13px' }}>Proveedor no asignado aún. Se asignará al aprobar en la Administración.</span>
              </div>
            )}
            {requerimiento.observacion_proveedor && (
              <div className="info-item full-width observacion-info-box">
                <label>Observaciones del Requerimiento / Proveedor:</label>
                <span>{requerimiento.observacion_proveedor}</span>
              </div>
            )}
          </div>
        </div>

        {/* Sección de Observación de Rechazo */}
        {requerimiento.observacion_rechazo && (
          <div className="detalle-seccion rechazo-seccion">
            <h3>❌ Observación de Rechazo</h3>
            <p className="rechazo-texto">{requerimiento.observacion_rechazo}</p>
          </div>
        )}

        {(requerimiento.cotizacion_archivo_url || requerimiento.imagen_producto_url) && (
          <div className="detalle-seccion">
            <h3>📎 Archivos Adjuntos</h3>
            <div className="archivos">
              {requerimiento.cotizacion_archivo_url && (
                <button
                  onClick={() => handleDownload(requerimiento.cotizacion_archivo_url, 'cotizacion')}
                  className="archivo-link"
                >
                  📄 Ver Cotización
                </button>
              )}
              {requerimiento.imagen_producto_url && (
                <button
                  onClick={() => handleDownload(requerimiento.imagen_producto_url, 'imagen_producto')}
                  className="archivo-link"
                >
                  🖼️ Ver Imagen del Producto
                </button>
              )}
            </div>
          </div>
        )}

        {/* Renderizado de panel de acciones para el usuario actual */}
        {renderActionPanel()}

        <div className="detalle-seccion">
          <h3>⏱️ Historial y Trazabilidad (Auditoría de Operaciones)</h3>
          <LineaTiempo eventos={requerimiento.linea_tiempo || []} />
        </div>
      </div>

      <Modal
        {...modalConfig}
        onClose={() => setModalConfig({ ...modalConfig, isOpen: false })}
      />

      {/* Modal Nueva Categoría */}
      {showNuevaCategoriaModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-alert-content" style={{ padding: '24px 24px 12px' }}>
              <h3 className="modal-title">Nueva Categoría de Proveedor</h3>
              <p className="modal-message">Defina un nuevo rubro o tipo de proveedor (ej: Tecnología, Construcción).</p>

              {errorCategoriaModal && <div className="error-message" style={{ width: '100%', marginTop: '12px' }}>⚠️ {errorCategoriaModal}</div>}

              <div className="form-group" style={{ width: '100%', marginTop: '16px', textAlign: 'left' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Nombre de la Categoría *</label>
                <input
                  type="text"
                  value={nuevaCategoriaNombre}
                  onChange={(e) => setNuevaCategoriaNombre(e.target.value)}
                  placeholder="Ej: Materiales de Oficina"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', marginTop: '4px' }}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="modal-btn modal-btn-cancel"
                onClick={() => setShowNuevaCategoriaModal(false)}
                disabled={cargandoNuevaCategoria}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="modal-btn modal-btn-confirm"
                onClick={handleCrearCategoria}
                disabled={cargandoNuevaCategoria || !nuevaCategoriaNombre.trim()}
              >
                {cargandoNuevaCategoria ? 'Creando...' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nuevo Proveedor */}
      {showNuevoProveedorModal && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: '480px' }}>
            <div className="modal-alert-content" style={{ padding: '24px 24px 12px' }}>
              <h3 className="modal-title">Nuevo Proveedor</h3>
              <p className="modal-message">Registre los datos comerciales del proveedor en la base de datos.</p>

              {errorProveedorModal && <div className="error-message" style={{ width: '100%', marginTop: '12px' }}>⚠️ {errorProveedorModal}</div>}

              <div className="form-group" style={{ width: '100%', marginTop: '16px', textAlign: 'left' }}>
                <label style={{ fontSize: '13px', fontWeight: '600' }}>Nombre del Proveedor *</label>
                <input
                  type="text"
                  value={nuevoProveedorData.nombre}
                  onChange={(e) => setNuevoProveedorData(prev => ({ ...prev, nombre: e.target.value }))}
                  placeholder="Ej: Inversiones 123 C.A."
                  style={{ width: '100%', marginTop: '4px' }}
                />
              </div>

              <div className="form-group" style={{ width: '100%', marginTop: '12px', textAlign: 'left' }}>
                <label style={{ fontSize: '13px', fontWeight: '600' }}>Categoría / Tipo *</label>
                <select
                  value={nuevoProveedorData.tipo}
                  onChange={(e) => setNuevoProveedorData(prev => ({ ...prev, tipo: e.target.value }))}
                  style={{ width: '100%', marginTop: '4px' }}
                >
                  <option value="">-- Seleccione categoría --</option>
                  {tiposProveedores.map(t => (
                    <option key={t.id} value={t.id}>{t.nombre}</option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ width: '100%', marginTop: '12px', textAlign: 'left' }}>
                <label style={{ fontSize: '13px', fontWeight: '600' }}>RIF *</label>
                <div className="cedula-input-group" style={{ marginTop: '4px' }}>
                  <select
                    value={nuevoProveedorData.tipo_documento}
                    onChange={(e) => setNuevoProveedorData(prev => ({ ...prev, tipo_documento: e.target.value }))}
                    className="select-nacionalidad"
                    style={{ marginTop: '4px' }}
                  >
                    {tiposDocumentos.map(td => (
                      <option key={td.id} value={td.id}>{td.sigla}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={nuevoProveedorData.rif_numero}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setNuevoProveedorData(prev => ({ ...prev, rif_numero: val }));
                    }}
                    placeholder="Número de RIF (sin letras)"
                    className="input-cedula-num"
                    style={{ marginTop: '4px' }}
                  />
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="modal-btn modal-btn-cancel"
                onClick={() => setShowNuevoProveedorModal(false)}
                disabled={cargandoNuevoProveedor}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="modal-btn modal-btn-confirm"
                onClick={handleCrearProveedor}
                disabled={cargandoNuevoProveedor || !nuevoProveedorData.tipo || !nuevoProveedorData.nombre || !nuevoProveedorData.tipo_documento || !nuevoProveedorData.rif_numero}
              >
                {cargandoNuevoProveedor ? 'Registrando...' : 'Registrar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetalleRequerimiento;
